import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const ToastContext = createContext({ show: () => {} });

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const hardRemove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const beginHide = useCallback((id) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true, visible: false } : t)));
    setTimeout(() => hardRemove(id), 220);
  }, [hardRemove]);

  const show = useCallback((arg, options = {}) => {
    const id = ++idRef.current;
    const isRich = typeof arg === 'object' && arg !== null;
    const title = isRich ? arg.title : undefined;
    const body = isRich ? arg.body : undefined;
    const message = isRich ? arg.message : String(arg || '');
    const variant = (isRich ? arg.variant : options.variant) || 'success';
    const durationRaw = isRich ? arg.duration : options.duration;
    const duration = typeof durationRaw === 'number' ? durationRaw : 3500;

    const toast = {
      id,
      title,
      body,
      message,
      variant,
      duration,
      visible: false,
      exiting: false
    };

    setToasts((prev) => {
      const next = [...prev, toast];
      return next.length > 3 ? next.slice(next.length - 3) : next;
    });

    // trigger enter transition on next tick
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, visible: true } : t)));
    }, 0);
    if (toast.duration > 0) setTimeout(() => beginHide(id), toast.duration);
    return id;
  }, [beginHide]);

  const value = useMemo(() => ({ show, remove: beginHide }), [show, beginHide]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="y-toasts" aria-live="polite" aria-atomic="true">
        {toasts.map((t) => {
          const stateClass = t.exiting ? 'exit' : t.visible ? 'enter' : '';
          const variantClass = `y-toast-${t.variant}`;
          return (
            <div key={t.id} role="status" className={`y-toast ${variantClass} ${stateClass}`}>
              {t.title || t.body ? (
                <div className="y-toast-content">
                  {t.title ? <div className="y-toast-title">{t.title}</div> : null}
                  {t.body ? <div className="y-toast-body">{t.body}</div> : null}
                </div>
              ) : (
                <div className="y-toast-message">{t.message}</div>
              )}
              <button type="button" aria-label="Close" className="y-toast-close" onClick={() => value.remove(t.id)}>
                ×
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}


